/**
 * `excerptEmbed { excerptId }` (#277): the block a destination body holds for
 * an excerpt sent to it. It stores only the pointer - the text lives in the
 * source document - so moving it is ordinary drag and drop and the
 * destination's autosave never carries a copy.
 *
 * It renders only while the excerpt is live and this document is still one of
 * its destinations (ExcerptEmbedView); otherwise it is invisible, which is
 * what lets an undo bring it back by restoring rows alone.
 *
 * Removing the block - Backspace on it, cutting it, deleting a range that
 * holds it - is "Remove from this item", never a delete of the source text: a
 * stray Backspace in a task must not erase meeting notes. The host is told
 * through `storage.onRemove` and drops the destination.
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node as PmNode } from '@tiptap/pm/model';
import { ExcerptEmbedView } from './ExcerptEmbedView';

export interface ExcerptEmbedStorage {
  /** The document this editor holds, so an embed knows whether it is still sent here. */
  host: { type: string; id: string } | null;
  /** Called with the ids of embeds a user edit removed. */
  onRemove: ((excerptIds: string[]) => void) | null;
  /** 0 for page editors; embeds nested inside an embed render as a plain link. */
  depth: number;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    excerptEmbed: {
      insertExcerptEmbed: (excerptId: string) => ReturnType;
    };
  }
  interface Storage {
    excerptEmbed: ExcerptEmbedStorage;
  }
}

/** Transactions carrying this meta (programmatic changes) never count as a user removal. */
export const EMBED_SILENT = 'excerptEmbedSilent';

export function embedIdsIn(doc: PmNode): string[] {
  const ids: string[] = [];
  doc.descendants((node) => {
    if (node.type.name === 'excerptEmbed' && node.attrs.excerptId) ids.push(node.attrs.excerptId as string);
    return node.type.name !== 'excerptEmbed';
  });
  return ids;
}

export const ExcerptEmbed = Node.create<Record<string, never>, ExcerptEmbedStorage>({
  name: 'excerptEmbed',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addStorage() {
    return { host: null, onRemove: null, depth: 0 };
  },

  addAttributes() {
    return {
      excerptId: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-excerpt-id'),
        renderHTML: (attrs: Record<string, unknown>) => (attrs.excerptId ? { 'data-excerpt-id': String(attrs.excerptId) } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="excerpt-embed"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'excerpt-embed' })];
  },

  renderText() {
    return '';
  },

  addNodeView() {
    return ReactNodeViewRenderer(ExcerptEmbedView);
  },

  addCommands() {
    return {
      insertExcerptEmbed: (excerptId) => ({ tr, state, dispatch }) => {
        if (embedIdsIn(state.doc).includes(excerptId)) return false;
        if (dispatch) {
          tr.insert(state.doc.content.size, state.schema.nodes.excerptEmbed.create({ excerptId }));
          tr.setMeta(EMBED_SILENT, true);
        }
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin({
        key: new PluginKey('excerptEmbedRemoval'),
        appendTransaction: (transactions, oldState, newState) => {
          if (!storage.onRemove) return null;
          const userEdit = transactions.some((tr) => tr.docChanged && !tr.getMeta(EMBED_SILENT) && !tr.getMeta('preventUpdate'));
          if (!userEdit) return null;
          const now = new Set(embedIdsIn(newState.doc));
          const removed = [...new Set(embedIdsIn(oldState.doc))].filter((id) => !now.has(id));
          if (removed.length) {
            const notify = storage.onRemove;
            // Outside the transaction cycle: the host makes a request and shows a toast.
            setTimeout(() => notify(removed), 0);
          }
          return null;
        },
      }),
    ];
  },
});
